/**
 * Role Simulator for Testing Phone Number Access Control
 * 
 * This utility allows you to simulate different user roles to test
 * the phone number masking functionality in the Driver Hires page.
 */

export type UserRole = 'viewer' | 'employee' | 'support' | 'manager' | 'supervisor' | 'admin';

export interface UserRoleConfig {
  role: UserRole;
  label: string;
  description: string;
  canViewPhoneNumbers: boolean;
  permissions: string[];
}

export const USER_ROLES: Record<UserRole, UserRoleConfig> = {
  viewer: {
    role: 'viewer',
    label: 'Viewer',
    description: 'Basic read-only access',
    canViewPhoneNumbers: false,
    permissions: ['read:bookings', 'read:basic_info']
  },
  employee: {
    role: 'employee',
    label: 'Employee',
    description: 'Standard employee access',
    canViewPhoneNumbers: false,
    permissions: ['read:bookings', 'read:basic_info', 'update:booking_status']
  },
  support: {
    role: 'support',
    label: 'Support',
    description: 'Customer support access',
    canViewPhoneNumbers: true,
    permissions: ['read:bookings', 'read:customer_info', 'read:phone_numbers', 'update:booking_status']
  },
  manager: {
    role: 'manager',
    label: 'Manager',
    description: 'Team manager access',
    canViewPhoneNumbers: true,
    permissions: ['read:all', 'write:bookings', 'read:phone_numbers', 'manage:team']
  },
  supervisor: {
    role: 'supervisor',
    label: 'Supervisor',
    description: 'Department supervisor access',
    canViewPhoneNumbers: true,
    permissions: ['read:all', 'write:all', 'read:phone_numbers', 'manage:department']
  },
  admin: {
    role: 'admin',
    label: 'Administrator',
    description: 'Full system access',
    canViewPhoneNumbers: true,
    permissions: ['*']
  }
};

/**
 * Set the current user role for testing
 */
export function setUserRole(role: UserRole): void {
  localStorage.setItem('userRole', role);
}

/**
 * Get the current user role
 */
export function getCurrentRole(): UserRole {
  return (localStorage.getItem('userRole') as UserRole) || 'viewer';
}

/**
 * Check if current user can view phone numbers
 */
export function canViewPhoneNumbers(): boolean {
  const role = getCurrentRole();
  return USER_ROLES[role]?.canViewPhoneNumbers || false;
}

/**
 * Get role configuration
 */
export function getRoleConfig(role?: UserRole): UserRoleConfig {
  const currentRole = role || getCurrentRole();
  return USER_ROLES[currentRole];
}

/**
 * Console helper to test different roles
 */
export function testRoles(): void {
  
  Object.entries(USER_ROLES).forEach(([key, config]) => {
  });
  
}

// Auto-run helper in development
if (typeof window !== 'undefined' && import.meta.env.MODE === 'development') {
  // Make functions available globally for console testing
  (window as any).setUserRole = setUserRole;
  (window as any).testRoles = testRoles;
  (window as any).getCurrentRole = getCurrentRole;
  (window as any).canViewPhoneNumbers = canViewPhoneNumbers;
  
}